import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";

/**
 * İletişim kişileri — firma portalı Ana Sayfa'sındaki "İletişim" widget'ının satırları (backlog #537, 2026-10-21; ön yüz yarısı
 * #538). Canlı, mock yok. Portal tarafı okuması firmaProfileService.getFirmaIletisimKisileri'dir
 * (GET /firma/{firmaId}/iletisim-kisileri); BU dosya yalnızca personelin yönettiği düz kaynaktır: /api/v1/iletisim-kisileri.
 *
 * Bir satırın KAPSAMI iki boş olabilen sütunundan çıkar: kapsam GENEL (fuarId ve firmaId yok: her fuardaki her firma),
 * FUAR (fuarId var: o fuardaki her firma), FIRMA (firmaId var; fuarId de varsa yalnızca o fuarda). Portal üçünü birleştirir:
 * önce firmanın kendi satırları, sonra fuarınkiler, sonra genel olanlar, her biri sıra'ya göre.
 *
 * Okuma yalnızca personel; yazma `iletisim_kisisi.manage` ister (PERMISSION_KEYS.ILETISIM_KISISI_MANAGE; Admin, kök her zaman).
 * Yetkisizde 403 ve mesajı yetkiyi adıyla söyler. PUT'un anlamı PATCH'tir: yalnızca gövdedeki anahtarlar değişir, null
 * gönderilen temizlenir (fuarId / firmaId null = her fuar / her firma, rolEn boş = İngilizce başlık kalkar).
 */
const BASE = `${JAVA_BACKEND_API_URL}/iletisim-kisileri`;

export const KAPSAM = { GENEL: 'GENEL', FUAR: 'FUAR', FIRMA: 'FIRMA' };

/** Alan sınırları (backend ILETISIM_ALAN_UZUN ile aynı): ad 100, rol 150, telefon 50, e-posta 150. */
export const ALAN_SINIRI = { adSoyad: 100, rol: 150, telefon: 50, eposta: 150 };

/** Backend'in e-posta denetimiyle aynı ölçüde gevşek: bir @ ve noktalı bir alan adı. */
export const EPOSTA_DESENI = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Hatalar bizim {message, result, data} zarfımızla gelir (iletisim.* mesajları, TR / EN) ya da Spring'in varsayılanıyla.
 * Durum koduna değil mesaja bakılır; 403'ün mesajı yetkiyi adıyla söyler ve olduğu gibi gösterilir.
 */
function iletisimHatasi(error, varsayilan) {
  const status = error?.response?.status ?? null;
  const data = error?.response?.data;
  let mesaj = varsayilan;
  if (status === 401) mesaj = i18n.t("services:iletisimKisisiService.oturumGecersiz");
  else if (data && typeof data === 'object' && data.message) mesaj = data.message;
  else if (data && typeof data === 'object' && data.error) mesaj = `${data.error}${data.status ? ` (${data.status})` : ''}`;
  else if (typeof data === 'string' && data.trim()) mesaj = data;
  else if (error?.message) mesaj = error.message;
  const err = new Error(mesaj);
  err.status = status;
  return err;
}

/** Bir satırı ekranın okuyacağı biçime getirir: boş metinler null, sayılar sayı. */
function satir(r) {
  const bos = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  return {
    ...r,
    id: Number(r.id),
    kapsam: r.kapsam || (r.firmaId != null ? KAPSAM.FIRMA : r.fuarId != null ? KAPSAM.FUAR : KAPSAM.GENEL),
    fuarId: r.fuarId != null ? Number(r.fuarId) : null,
    firmaId: r.firmaId != null ? Number(r.firmaId) : null,
    rolTr: bos(r.rolTr),
    rolEn: bos(r.rolEn),
    adSoyad: bos(r.adSoyad),
    telefon: bos(r.telefon),
    eposta: bos(r.eposta),
    sira: Number(r.sira) || 0,
    aktif: r.aktif !== false && Number(r.aktif) !== 0,
  };
}

/**
 * GET /iletisim-kisileri — her satır, gizli olanlar dahil. Süzgeçler (tam eşleşme) isteğe bağlı: fuarId, firmaId, kapsam.
 * Dönen: [{ id, kapsam, fuarId, fuarAdi, firmaId, firmaAdi, role, rolTr, rolEn, adSoyad, telefon, eposta, sira, aktif,
 * eklenmeTarihi, guncellemeTarihi, guncelleyenAdSoyad, ... }]
 */
export async function listIletisimKisileri({ fuarId, firmaId, kapsam } = {}) {
  const params = {};
  if (fuarId) params.fuarId = fuarId;
  if (firmaId) params.firmaId = firmaId;
  if (kapsam) params.kapsam = kapsam;
  try {
    const res = await axios.get(BASE, { params, headers: getAuthHeader() });
    return (Array.isArray(res.data) ? res.data : []).map(satir);
  } catch (error) {
    throw iletisimHatasi(error, i18n.t("services:iletisimKisisiService.listeAlinamadi"));
  }
}

/**
 * Formdaki değerlerden istek gövdesi. Her anahtar HER ZAMAN gönderilir: PUT PATCH gibi çalıştığı için kapsamı genele
 * çevirmek fuarId / firmaId'nin açıkça null gitmesini ister. Boş metin backend'de null sayılır.
 */
export function iletisimGovdesi(f) {
  const govde = {
    fuarId: f.kapsam === KAPSAM.GENEL || !f.fuarId ? null : Number(f.fuarId),
    firmaId: f.kapsam === KAPSAM.FIRMA && f.firmaId ? Number(f.firmaId) : null,
    rolTr: (f.rolTr || '').trim() || null,
    rolEn: (f.rolEn || '').trim() || null,
    adSoyad: (f.adSoyad || '').trim(),
    telefon: (f.telefon || '').trim() || null,
    eposta: (f.eposta || '').trim() || null,
    aktif: f.aktif !== false,
  };
  if (f.sira !== '' && f.sira != null && Number.isFinite(Number(f.sira))) govde.sira = Number(f.sira);
  return govde;
}

/** POST /iletisim-kisileri — yeni satır. Dönen: oluşan satır. */
export async function createIletisimKisisi(f) {
  try {
    const res = await axios.post(BASE, iletisimGovdesi(f), { headers: getAuthHeader() });
    return res.data && typeof res.data === 'object' ? satir(res.data) : res.data;
  } catch (error) {
    throw iletisimHatasi(error, i18n.t("services:iletisimKisisiService.kaydedilemedi"));
  }
}

/** PUT /iletisim-kisileri/{id} — satırı günceller (formdaki her alan gönderilir). */
export async function updateIletisimKisisi(id, f) {
  try {
    const res = await axios.put(`${BASE}/${id}`, iletisimGovdesi(f), { headers: getAuthHeader() });
    return res.data && typeof res.data === 'object' ? satir(res.data) : res.data;
  } catch (error) {
    throw iletisimHatasi(error, i18n.t("services:iletisimKisisiService.kaydedilemedi"));
  }
}

/** PUT /iletisim-kisileri/{id} {aktif} — satırı portaldan gizler / yeniden gösterir; yalnızca bu anahtar değişir. */
export async function setIletisimKisisiAktif(id, aktif) {
  try {
    const res = await axios.put(`${BASE}/${id}`, { aktif: Boolean(aktif) }, { headers: getAuthHeader() });
    return res.data && typeof res.data === 'object' ? satir(res.data) : res.data;
  } catch (error) {
    throw iletisimHatasi(error, i18n.t("services:iletisimKisisiService.kaydedilemedi"));
  }
}

/** DELETE /iletisim-kisileri/{id} — satırı ve İngilizce başlığını siler (gizlemek için setIletisimKisisiAktif). */
export async function deleteIletisimKisisi(id) {
  try {
    await axios.delete(`${BASE}/${id}`, { headers: getAuthHeader() });
  } catch (error) {
    throw iletisimHatasi(error, i18n.t("services:iletisimKisisiService.silinemedi"));
  }
}
